import React, { useEffect, useRef, useState } from "react";
import { AlertTriangle, Bell, CheckCircle2, Inbox, XCircle } from "lucide-react";
import { useHistory } from "react-router-dom";
import { Button } from "@dhn/components/ui/button";
import { cn } from "@dhn/lib/utils";
import useUserNotifications from "./use-user-notifications";

const formatRelativeDate = (value) => {
  const date = new Date(value);
  const elapsedMinutes = Math.max(0, Math.floor((Date.now() - date.getTime()) / 60000));
  if (!Number.isFinite(elapsedMinutes) || elapsedMinutes < 1) return "Ahora";
  if (elapsedMinutes < 60) return `Hace ${elapsedMinutes} min`;
  const elapsedHours = Math.floor(elapsedMinutes / 60);
  if (elapsedHours < 24) return `Hace ${elapsedHours} h`;
  const elapsedDays = Math.floor(elapsedHours / 24);
  if (elapsedDays < 7) return `Hace ${elapsedDays} d`;
  return date.toLocaleDateString("es-DO", { day: "2-digit", month: "short" });
};

// El sufijo del tipo define el tono visual de la notificacion.
const TONES = [
  { suffixes: ["_REJECTED", "_FAILED", "_EXHAUSTED"], className: "is-rejected", Icon: XCircle },
  { suffixes: ["_UPCOMING", "_EXPIRING"], className: "is-warning", Icon: AlertTriangle },
];
const DEFAULT_TONE = { className: "is-approved", Icon: CheckCircle2 };

const getTone = (type = "") =>
  TONES.find(({ suffixes }) => suffixes.some((suffix) => type.endsWith(suffix))) || DEFAULT_TONE;

export default function UserNotifications() {
  const history = useHistory();
  const containerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const {
    notifications,
    unreadCount,
    loading,
    markingAll,
    readNotification,
    readAllNotifications,
  } = useUserNotifications();

  useEffect(() => {
    if (!isOpen) return undefined;
    const closeOutside = (event) => {
      if (!containerRef.current?.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", closeOutside);
    return () => document.removeEventListener("mousedown", closeOutside);
  }, [isOpen]);

  const openNotification = async (notification) => {
    try {
      await readNotification(notification);
    } finally {
      setIsOpen(false);
      if (notification.actionUrl) history.push(notification.actionUrl);
    }
  };

  return (
    <div className="user-notifications" ref={containerRef}>
      <Button
        type="button"
        variant="outline"
        size="icon"
        className={cn("user-notifications__trigger", isOpen && "is-open")}
        onClick={() => setIsOpen((value) => !value)}
        aria-label={`Notificaciones${unreadCount ? `, ${unreadCount} sin leer` : ""}`}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span className="user-notifications__count">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </Button>

      {isOpen && (
        <section className="user-notifications__panel" aria-label="Notificaciones">
          <header className="user-notifications__header">
            <div>
              <strong>Notificaciones</strong>
              <small>{unreadCount ? `${unreadCount} sin leer` : "Todo al día"}</small>
            </div>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={readAllNotifications}
                disabled={markingAll}
              >
                <i
                  className={`fa ${markingAll ? "fa-spinner fa-spin" : "fa-check"}`}
                  aria-hidden="true"
                ></i>
                {markingAll ? " Marcando..." : " Marcar todas como leídas"}
              </button>
            )}
          </header>

          <div className="user-notifications__list">
            {loading ? (
              <div className="user-notifications__empty">Cargando notificaciones...</div>
            ) : notifications.length ? (
              notifications.map((notification) => {
                const { className: toneClassName, Icon } = getTone(notification.type || "");
                return (
                  <button
                    type="button"
                    key={notification.id}
                    className={cn(
                      "user-notifications__item",
                      !notification.readAt && "is-unread",
                    )}
                    onClick={() => openNotification(notification)}
                  >
                    <span
                      className={cn(
                        "user-notifications__icon",
                        toneClassName,
                      )}
                    >
                      <Icon size={18} />
                    </span>
                    <span className="user-notifications__content">
                      <strong>{notification.title}</strong>
                      <span>{notification.message}</span>
                      <small>{formatRelativeDate(notification.createdAt)}</small>
                    </span>
                    {!notification.readAt && <span className="user-notifications__unread-dot" />}
                  </button>
                );
              })
            ) : (
              <div className="user-notifications__empty">
                <Inbox size={28} />
                <strong>No tienes notificaciones</strong>
                <span>Las decisiones sobre tus solicitudes aparecerán aquí.</span>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
